/**
 * VocabRight Data Service
 * 
 * Unified data service that tries A2A first, falls back to Supabase
 */

import FeatureFlags from '../utils/featureFlags';
import a2aVocabRightService from '../api/services/a2aVocabRightService';
import supabaseClient from './supabaseClient';

/**
 * VocabRight Data Service Class
 */
class VocabRightDataService {
  constructor() {
    this.useA2A = FeatureFlags.USE_A2A_BACKEND;
    this.fallbackToSupabase = FeatureFlags.FALLBACK_TO_SUPABASE;
    this.enableLogging = FeatureFlags.ENABLE_LOGGING;
    
    // Always log initialization (even in production) for debugging
    console.log('[VocabRightDataService] Initialized', {
      useA2A: this.useA2A,
      fallbackToSupabase: this.fallbackToSupabase,
      featureFlagValue: FeatureFlags.USE_A2A_BACKEND,
      envValue: process.env.REACT_APP_USE_A2A_BACKEND
    });
  }

  /**
   * Try A2A first, fallback to Supabase if enabled
   */
  async tryWithFallback(a2aMethod, supabaseFallback, methodName) {
    // Try A2A first if enabled
    if (this.useA2A) {
      try {
        console.log(`[VocabRightDataService] ${methodName}: Using A2A backend`);
        const result = await a2aMethod();
        console.log(`[VocabRightDataService] ${methodName}: A2A success`);
        return result;
      } catch (error) {
        const errorDetails = {
          error: error.message,
          name: error.name,
          code: error.code,
          isNetworkError: error.message && (
            error.message.includes('Failed to fetch') ||
            error.message.includes('ERR_CONNECTION_REFUSED') ||
            error.message.includes('NETWORK_ERROR') ||
            error.message.includes('Invalid URL') ||
            error.message.includes('CORS')
          )
        };
        
        console.error(`[VocabRightDataService] ${methodName}: A2A call failed`, errorDetails);
        
        const isConnectionError = errorDetails.isNetworkError;
        const fallbackAvailable = this.fallbackToSupabase && supabaseFallback;
        
        if (!isConnectionError || !fallbackAvailable) {
          throw error;
        } else {
          console.warn(`[VocabRightDataService] ${methodName}: A2A backend error (${error.message}), using Supabase fallback`);
        }
        
        // Fallback to Supabase if enabled
        if (fallbackAvailable) {
          console.log(`[VocabRightDataService] ${methodName}: Falling back to Supabase`);
          try {
            const result = await supabaseFallback();
            console.log(`[VocabRightDataService] ${methodName}: Supabase fallback success`);
            return result;
          } catch (supabaseError) {
            console.error(`[VocabRightDataService] ${methodName}: Supabase fallback failed`, supabaseError);
            throw supabaseError;
          }
        }
        
        throw error;
      }
    }
    
    // Use Supabase directly if A2A is disabled
    if (supabaseFallback) {
      console.warn(`[VocabRightDataService] ${methodName}: A2A disabled (useA2A=${this.useA2A}), using Supabase`);
      return await supabaseFallback();
    }
    
    throw new Error(`${methodName}: A2A disabled and no Supabase fallback available`);
  }

  /**
   * Get vocabulary sets
   * Try A2A, fallback to Supabase
   */
  async getVocabularySets(params = {}) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aVocabRightService.getVocabularySets(params);
        return result;
      },
      // Supabase fallback
      async () => {
        const data = await supabaseClient.getAll('eng_vocabrightsets');
        // Transform Supabase data to match expected format
        return data.map((row, index) => ({
          id: row.set_id,
          title: row.title,
          titleTraditional: row.title_traditional,
          titleEnglish: row.title_english,
          category: row.category,
          categoryEnglish: row.category_english,
          color: row.color,
          imageUrl: row.image_url,
          words: row.words || [],
          studyMode: row.study_mode || false,
          testMode: row.test_mode || false,
          originalIndex: index
        }));
      },
      'getVocabularySets'
    );
  }

  /**
   * Get user progress
   * Try A2A, fallback to Supabase
   */
  async getUserProgress(params = {}) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aVocabRightService.getUserProgress(params);
        return result;
      },
      // Supabase fallback - return default progress structure
      async () => {
        // For now, return default structure
        // In future, can fetch from Supabase if progress table exists
        return {
          completedSets: [],
          calendarData: {}
        };
      },
      'getUserProgress'
    );
  }

  /**
   * Save user progress
   * Try A2A, fallback to Supabase
   */
  async saveUserProgress(progressData) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aVocabRightService.saveUserProgress(progressData);
        return result;
      },
      // Supabase fallback - save to localStorage for now
      async () => {
        // Save to localStorage as fallback
        if (typeof window !== 'undefined') {
          localStorage.setItem('vocabRightProgress', JSON.stringify(progressData));
        }
        return progressData;
      },
      'saveUserProgress'
    );
  }

  /**
   * Get calendar data
   * Try A2A, fallback to Supabase
   */
  async getCalendarData(params = {}) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aVocabRightService.getCalendarData(params);
        return result;
      },
      // Supabase fallback - extract from progress
      async () => {
        // Extract calendar data from progress if available
        const progress = JSON.parse(localStorage.getItem('vocabRightProgress') || '{}');
        return progress.calendarData || {};
      },
      'getCalendarData'
    );
  }
}

// Create singleton instance
const vocabRightDataService = new VocabRightDataService();

export default vocabRightDataService;

